import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  EyeOff,
  Loader2,
  MapPin,
  Plus,
  RotateCcw,
} from 'lucide-react';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

const toDateOnly = (date) => date.toISOString().slice(0, 10);

const monthLabel = (date) => (
  new Intl.DateTimeFormat('es-AR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(date)
);

const dayNumber = (date) => (
  new Intl.DateTimeFormat('es-AR', { day: '2-digit', timeZone: 'UTC' }).format(date)
);

const formatEventDate = (dateOnly) => (
  new Intl.DateTimeFormat('es-AR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${dateOnly}T00:00:00Z`))
);

const formatTime = (event) => {
  if (event.allDay || !event.startTime || !event.endTime) return 'Dia completo';
  return `${event.startTime} - ${event.endTime}`;
};

const getEventStyle = (event) => {
  if (event.salesMode !== 'booking') {
    return event.isPublic === false
      ? 'bg-blue-500/10 border-blue-500/20 text-blue-200'
      : 'bg-primary-orange/10 border-primary-orange/20 text-orange-100';
  }

  if (event.bookingPaymentStatus === 'paid') return 'bg-green-500/10 border-green-500/20 text-green-200';
  if (event.bookingPaymentStatus === 'partial') return 'bg-yellow-500/10 border-yellow-500/20 text-yellow-100';
  return 'bg-red-500/10 border-red-500/20 text-red-100';
};

const getEventBadge = (event) => {
  if (event.salesMode !== 'booking') return event.isPublic === false ? 'Privado' : 'Tickets';
  if (event.bookingPaymentStatus === 'paid') return 'Cobrado';
  if (event.bookingPaymentStatus === 'partial') return 'Seña';
  return 'Pendiente';
};

const buildMonthGrid = (viewDate) => {
  const firstOfMonth = new Date(Date.UTC(viewDate.getUTCFullYear(), viewDate.getUTCMonth(), 1));
  const offset = (firstOfMonth.getUTCDay() + 6) % 7;
  const gridStart = new Date(firstOfMonth.getTime() - offset * DAY_MS);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart.getTime() + index * DAY_MS);
    return {
      date,
      dateOnly: toDateOnly(date),
      inMonth: date.getUTCMonth() === viewDate.getUTCMonth(),
    };
  });
};

const AdminAgenda = () => {
  const { slug } = useParams();
  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const days = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const from = days[0]?.dateOnly;
  const to = days[days.length - 1]?.dateOnly;

  useEffect(() => {
    let alive = true;

    const fetchAgenda = async () => {
      if (!slug || !from || !to) return;
      setLoading(true);
      setError('');

      try {
        const { data } = await axios.get(`/${slug}/api/events/agenda`, {
          params: { from, to },
        });
        if (alive) setEvents(data.events || []);
      } catch (err) {
        if (alive) {
          setError(err.response?.data?.error || 'No se pudo cargar la agenda.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchAgenda();
    return () => {
      alive = false;
    };
  }, [slug, from, to]);

  const eventsByDay = useMemo(() => {
    return events.reduce((acc, event) => {
      const key = event.dateOnly || (event.date ? event.date.slice(0, 10) : '');
      if (!key) return acc;
      acc[key] = acc[key] || [];
      acc[key].push(event);
      return acc;
    }, {});
  }, [events]);

  const todayKey = toDateOnly(new Date());
  const monthEvents = events.filter((event) => {
    const eventDate = event.dateOnly || event.date?.slice(0, 10);
    if (!eventDate) return false;
    const date = new Date(`${eventDate}T00:00:00Z`);
    return date.getUTCMonth() === viewDate.getUTCMonth() && date.getUTCFullYear() === viewDate.getUTCFullYear();
  });
  const occupiedDays = new Set(monthEvents.map((event) => event.dateOnly || event.date?.slice(0, 10))).size;
  const upcomingEvents = monthEvents
    .filter((event) => (event.dateOnly || event.date?.slice(0, 10)) >= todayKey)
    .sort((a, b) => `${a.dateOnly}${a.startTime}`.localeCompare(`${b.dateOnly}${b.startTime}`));

  const changeMonth = (amount) => {
    setViewDate((current) => new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + amount, 1)));
  };

  const goToday = () => {
    const now = new Date();
    setViewDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)));
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 space-y-8">
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
              <CalendarDays size={14} className="text-primary-orange" />
              <span className="text-[10px] font-black uppercase tracking-[2px] text-white/50">Administracion</span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-black uppercase tracking-tight">
              Agenda <span className="text-gradient">Eventos</span>
            </h1>
            <p className="text-white/40 font-medium mt-2">
              Visualiza fechas ocupadas y evita reservas superpuestas por salon.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center"
              aria-label="Mes anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={goToday}
              className="h-12 px-4 rounded-2xl bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 flex items-center gap-2 font-bold"
            >
              <RotateCcw size={16} />
              Hoy
            </button>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              className="h-12 w-12 rounded-2xl bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center"
              aria-label="Mes siguiente"
            >
              <ChevronRight size={20} />
            </button>
            <Link
              to={`/${slug}/admin/events/create`}
              className="h-12 px-5 rounded-2xl bg-brand-gradient text-white font-black flex items-center gap-2 shadow-lg shadow-orange-500/20"
            >
              <Plus size={18} />
              Crear evento
            </Link>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Mes" value={monthLabel(viewDate)} />
          <Stat label="Fechas ocupadas" value={occupiedDays} />
          <Stat label="Eventos del mes" value={monthEvents.length} />
        </section>

        {error && (
          <div className="rounded-3xl border border-red-500/20 bg-red-500/10 px-5 py-4 text-red-300 font-bold">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-6">
          <section className="bg-glass rounded-[32px] border border-white/5 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-white/5 bg-white/[0.02]">
              {WEEKDAYS.map((day) => (
                <div key={day} className="px-2 py-3 text-center text-[10px] font-black uppercase tracking-[2px] text-white/35">
                  {day}
                </div>
              ))}
            </div>

            {loading ? (
              <div className="h-[560px] flex items-center justify-center text-primary-orange">
                <Loader2 className="animate-spin" size={36} />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {days.map((day) => {
                  const dayEvents = eventsByDay[day.dateOnly] || [];
                  const occupied = dayEvents.length > 0;
                  const isToday = day.dateOnly === todayKey;

                  return (
                    <div
                      key={day.dateOnly}
                      className={`min-h-[118px] border-r border-b border-white/5 p-2 sm:p-3 ${day.inMonth ? 'bg-transparent' : 'bg-black/20 opacity-45'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`h-7 min-w-7 px-2 rounded-full flex items-center justify-center text-xs font-black ${isToday ? 'bg-primary-orange text-black' : 'text-white/60'}`}>
                          {dayNumber(day.date)}
                        </span>
                        {occupied && (
                          <span className="hidden sm:inline text-[9px] font-black uppercase text-primary-orange">
                            Ocupada
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        {dayEvents.slice(0, 3).map((event) => (
                          <Link
                            key={event._id}
                            to={`/${slug}/admin/events/edit/${event._id}`}
                            className={`block rounded-xl px-2 py-1.5 border text-[10px] leading-tight transition-colors ${getEventStyle(event)} hover:bg-white/10`}
                            title={event.title}
                          >
                            <span className="block font-black truncate">{event.title}</span>
                            <span className="block text-white/45 truncate">{formatTime(event)}</span>
                          </Link>
                        ))}
                        {dayEvents.length > 3 && (
                          <p className="text-[10px] text-white/30 font-bold">+{dayEvents.length - 3} mas</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="bg-glass rounded-[32px] border border-white/5 p-5 h-fit">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[2px] text-white/30">Proximas fechas</p>
                <h2 className="text-xl font-black text-white">Reservas</h2>
              </div>
              <CalendarDays className="text-primary-orange" size={24} />
            </div>

            {upcomingEvents.length === 0 ? (
              <div className="rounded-2xl bg-white/5 border border-white/5 p-5 text-center text-white/35 text-sm font-bold">
                No hay eventos futuros este mes.
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <Link
                    key={event._id}
                    to={`/${slug}/admin/events/edit/${event._id}`}
                    className="block rounded-2xl bg-white/[0.03] border border-white/5 p-4 hover:bg-white/[0.06] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-[2px] text-primary-orange mb-1">
                          {formatEventDate(event.dateOnly)}
                        </p>
                        <h3 className="font-black text-white truncate">{event.title}</h3>
                        <p className="text-[10px] font-black uppercase tracking-widest text-white/25 mt-1">
                          {getEventBadge(event)}
                        </p>
                      </div>
                      {event.isPublic === false && <EyeOff size={16} className="text-blue-300 shrink-0" />}
                    </div>
                    <div className="mt-3 space-y-2 text-xs font-bold text-white/45">
                      <p className="flex items-center gap-2">
                        <Clock size={13} className="text-white/25" />
                        {formatTime(event)}
                      </p>
                      {(event.city || event.location) && (
                        <p className="flex items-center gap-2 truncate">
                          <MapPin size={13} className="text-white/25 shrink-0" />
                          <span className="truncate">{event.city || event.location}</span>
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-3xl bg-glass border border-white/5 px-5 py-4">
    <p className="text-[10px] font-black uppercase tracking-[2px] text-white/30 mb-1">{label}</p>
    <p className="text-2xl font-black text-white capitalize">{value}</p>
  </div>
);

export default AdminAgenda;
